import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import { extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';
import EmptyState from '../components/EmptyState';
import BarcodeScanner from '../components/BarcodeScanner';
import { useProducts, useCreateProduct, useUpdateProduct, useDeleteProduct } from '../hooks/useProducts';
import { useCategories } from '../hooks/useCategories';

const Products = () => {
  const { selectedStore, isModuleEnabled } = useStore();
  const showToast = useToast();

  const productsQuery = useProducts();
  const categoriesQuery = useCategories();
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const products = productsQuery.data ?? [];
  const categories = categoriesQuery.data ?? [];
  const loading = productsQuery.isLoading;

  const [mutationError, setMutationError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    name: '', sku: '', description: '', category_id: '', cost_price: 0,
    selling_price: 0, rental_price: '', reorder_level: 10, is_rentable: false,
    vat_percentage: 0, initial_stock: 0,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMutationError(null);
    try {
      const data = { ...formData, store_id: selectedStore.id };
      if (!data.rental_price) delete data.rental_price;
      if (editingId) {
        await updateProduct.mutateAsync({ id: editingId, data });
        showToast('Product updated successfully', 'success');
      } else {
        await createProduct.mutateAsync(data);
        showToast('Product created successfully', 'success');
      }
      resetForm();
    } catch (err) {
      setMutationError(extractErrorMessage(err, 'Operation failed'));
    }
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name,
      sku: product.sku,
      description: product.description || '',
      category_id: product.category_id,
      cost_price: product.cost_price,
      selling_price: product.selling_price,
      rental_price: product.rental_price || '',
      reorder_level: product.reorder_level,
      is_rentable: product.is_rentable,
      vat_percentage: product.vat_percentage,
      initial_stock: 0,
    });
    setEditingId(product.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteProduct.mutateAsync(id);
      showToast('Product deleted', 'success');
    } catch (err) {
      setMutationError(extractErrorMessage(err, 'Failed to delete product'));
    }
  };

  const handleBarcodeDetected = (code) => {
    setFormData(prev => ({ ...prev, sku: code }));
    setShowForm(true);
    setShowScanner(false);
  };

  const resetForm = () => {
    setFormData({
      name: '', sku: '', description: '', category_id: '', cost_price: 0,
      selling_price: 0, rental_price: '', reorder_level: 10, is_rentable: false,
      vat_percentage: 0, initial_stock: 0,
    });
    setEditingId(null);
    setShowForm(false);
    setMutationError(null);
  };

  // Helper functions
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedStore?.currency || 'USD',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  // Icon helper
  const renderIcon = (iconName) => {
    const icons = {
      box: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
          <line x1="12" y1="22.08" x2="12" y2="12"></line>
        </svg>
      ),
      check: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      ),
      alert: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      ),
      x: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      ),
      package: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
          <line x1="12" y1="22.08" x2="12" y2="12"></line>
        </svg>
      ),
      edit: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
        </svg>
      ),
      trash: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        </svg>
      ),
    };
    return icons[iconName] || null;
  };

  // Calculate stats
  const stats = {
    total: products.length,
    active: products.filter(p => p.quantity_in_stock > 0).length,
    lowStock: products.filter(p => p.quantity_in_stock > 0 && p.quantity_in_stock <= p.reorder_level).length,
    outOfStock: products.filter(p => p.quantity_in_stock === 0).length,
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = (product.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (product.sku || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || product.category_id === parseInt(filterCategory);
    const matchesStatus = filterStatus === 'all' ||
                         (filterStatus === 'in-stock' && product.quantity_in_stock > 0) ||
                         (filterStatus === 'low-stock' && product.quantity_in_stock > 0 && product.quantity_in_stock <= product.reorder_level) ||
                         (filterStatus === 'out-of-stock' && product.quantity_in_stock === 0);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Get stock badge class
  const getStockBadge = (product) => {
    if (product.quantity_in_stock === 0) return 'badge-danger';
    if (product.quantity_in_stock <= product.reorder_level) return 'badge-warning';
    return 'badge-success';
  };

  const getStockLabel = (product) => {
    if (product.quantity_in_stock === 0) return 'Out of Stock';
    if (product.quantity_in_stock <= product.reorder_level) return 'Low Stock';
    return 'In Stock';
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Loading products...</span>
      </div>
    );
  }

  if (productsQuery.isError && products.length === 0) {
    return <div className="alert alert-danger">{extractErrorMessage(productsQuery.error, 'Failed to load products')}</div>;
  }

  // Show focused empty state when no products exist and form isn't open
  if (!loading && products.length === 0 && !showForm) {
    return (
      <div className="page-modern">
        <div className="page-header">
          <div>
            <h1 className="page-title">Products</h1>
            <p className="page-subtitle">Manage your product catalogue and inventory</p>
          </div>
          <button className="btn btn-secondary" onClick={() => setShowScanner(true)}>
            📷 Scan
          </button>
        </div>
        {showScanner && (
          <BarcodeScanner
            onDetected={handleBarcodeDetected}
            onClose={() => setShowScanner(false)}
          />
        )}
        {mutationError && <div className="alert alert-danger">{mutationError}</div>}
        <EmptyState
          icon="📦"
          title="No products yet"
          message="Add your first product to start tracking stock levels, recording sales, and generating reports."
          action="+ Add your first product"
          actionPath="/products/new"
        />
      </div>
    );
  }

  return (
    <div className="page-modern">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Products</h1>
          <p className="page-subtitle">Manage your product catalogue and inventory</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setShowScanner(true)}>
            📷 Scan
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
          >
            {showForm ? 'Cancel' : '+ Add Product'}
          </button>
        </div>
      </div>
      {showScanner && (
        <BarcodeScanner
          onDetected={handleBarcodeDetected}
          onClose={() => setShowScanner(false)}
        />
      )}

      {mutationError && <div className="alert alert-danger">{mutationError}</div>}

      {/* Stats Row */}
      <div className="page-stats-row">
        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--primary)' }}>
            {renderIcon('box')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Total Products</div>
            <div className="page-stat-value">{formatNumber(stats.total)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--success)' }}>
            {renderIcon('check')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">In Stock</div>
            <div className="page-stat-value">{formatNumber(stats.active)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--warning)' }}>
            {renderIcon('alert')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Low Stock</div>
            <div className="page-stat-value">{formatNumber(stats.lowStock)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--danger)' }}>
            {renderIcon('x')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Out of Stock</div>
            <div className="page-stat-value">{formatNumber(stats.outOfStock)}</div>
          </div>
        </div>
      </div>

      {/* Form Card */}
      {showForm && (
        <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
          <div className="card-header">
            <h3 className="card-title">{editingId ? 'Edit Product' : 'Add New Product'}</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Product Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Enter product name"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">SKU *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    required
                    placeholder="e.g., PRD-001"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select
                    className="form-select"
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: parseInt(e.target.value) })}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Reorder Level</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.reorder_level}
                    onChange={(e) => setFormData({ ...formData, reorder_level: parseInt(e.target.value) || 0 })}
                    min="0"
                    placeholder="10"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Cost Price</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.cost_price}
                    onChange={(e) => setFormData({ ...formData, cost_price: parseFloat(e.target.value) || 0 })}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Selling Price *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.selling_price}
                    onChange={(e) => setFormData({ ...formData, selling_price: parseFloat(e.target.value) || 0 })}
                    step="0.01"
                    min="0"
                    required
                    placeholder="0.00"
                  />
                </div>

                {isModuleEnabled('rental') && (
                  <div className="form-group">
                    <label className="form-label">Rental Price</label>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.rental_price}
                      onChange={(e) => setFormData({ ...formData, rental_price: parseFloat(e.target.value) || '' })}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">VAT %</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.vat_percentage}
                    onChange={(e) => setFormData({ ...formData, vat_percentage: parseFloat(e.target.value) || 0 })}
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  placeholder="Optional product description"
                ></textarea>
              </div>

              {!editingId && (
                <div className="form-group">
                  <label className="form-label">Initial Stock Quantity</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.initial_stock}
                    onChange={(e) => setFormData({ ...formData, initial_stock: parseInt(e.target.value) || 0 })}
                    min="0"
                    placeholder="0"
                  />
                  <span className="form-help-text">Set initial stock quantity (can be updated later in Inventory)</span>
                </div>
              )}

              {isModuleEnabled('rental') && (
                <div className="form-checkbox">
                  <input
                    type="checkbox"
                    id="is_rentable"
                    checked={formData.is_rentable}
                    onChange={(e) => setFormData({ ...formData, is_rentable: e.target.checked })}
                  />
                  <label htmlFor="is_rentable">This product is available for rental</label>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createProduct.isPending || updateProduct.isPending}
                >
                  {editingId ? 'Save Changes' : 'Create Product'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      {products.length > 0 && (
        <div className="card page-filter-card">
          <div className="page-filter-grid">
            <div className="form-group">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by name or SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Stock Status</label>
              <select
                className="form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="in-stock">In Stock</option>
                <option value="low-stock">Low Stock</option>
                <option value="out-of-stock">Out of Stock</option>
              </select>
            </div>

            {(searchTerm || filterCategory || filterStatus !== 'all') && (
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setSearchTerm('');
                  setFilterCategory('');
                  setFilterStatus('all');
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Products Table/List */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            All Products ({filteredProducts.length}{filteredProducts.length !== products.length && ` of ${products.length}`})
          </h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {filteredProducts.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="table-container hidden-mobile">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Category</th>
                      <th style={{ textAlign: 'right' }}>Price</th>
                      <th style={{ textAlign: 'center' }}>Stock</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map(product => (
                      <tr key={product.id}>
                        <td className="font-medium">{product.name}</td>
                        <td className="text-muted">{product.sku}</td>
                        <td>{product.category_name}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(product.selling_price)}</td>
                        <td style={{ textAlign: 'center' }} className="text-muted">{product.quantity_in_stock || 0}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`badge ${getStockBadge(product)}`}>
                            {getStockLabel(product)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div className="table-actions">
                            <button
                              className="table-action-btn"
                              onClick={() => handleEdit(product)}
                              title="Edit"
                            >
                              {renderIcon('edit')}
                            </button>
                            <button
                              className="table-action-btn table-action-btn-danger"
                              onClick={() => handleDelete(product.id)}
                              title="Delete"
                              disabled={deleteProduct.isPending}
                            >
                              {renderIcon('trash')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards hidden-desktop">
                {filteredProducts.map(product => (
                  <div key={product.id} className="mobile-card">
                    <div className="mobile-card-header">
                      <div className="mobile-card-title">{product.name}</div>
                      <span className={`badge ${getStockBadge(product)}`}>
                        {getStockLabel(product)}
                      </span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">SKU</span>
                      <span>{product.sku}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Category</span>
                      <span>{product.category_name}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Price</span>
                      <span className="font-medium">{formatCurrency(product.selling_price)}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Stock</span>
                      <span>{product.quantity_in_stock || 0}</span>
                    </div>
                    <div className="mobile-card-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(product)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(product.id)}
                        disabled={deleteProduct.isPending}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : products.length === 0 ? (
            <div className="page-empty-state">
              <div className="page-empty-icon">{renderIcon('package')}</div>
              <div className="page-empty-title">No products yet</div>
              <p className="page-empty-text">
                Start building your catalog by adding your first product
              </p>
              <button
                className="btn btn-primary"
                style={{ marginTop: '1.5rem' }}
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
              >
                Add Your First Product
              </button>
            </div>
          ) : (
            <div className="page-empty-state">
              <div className="page-empty-icon">{renderIcon('package')}</div>
              <div className="page-empty-title">No products found</div>
              <p className="page-empty-text">
                Try adjusting your filters or search term
              </p>
              <button
                className="btn btn-secondary"
                style={{ marginTop: '1.5rem' }}
                onClick={() => {
                  setSearchTerm('');
                  setFilterCategory('');
                  setFilterStatus('all');
                }}
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;
